import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import CopyAvailabilityModal from '../components/admin/CopyAvailabilityModal';
import LoadingSkeleton from '../components/LoadingSkeleton';
import { ArrowLeft, Plus, ChevronDown, ChevronRight, Calendar, Trash2, Copy, Edit } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_EMAILS = ["julie@jcp-teaching.com", "test@example.com", "jc-p@outlook.de"];

const AdminAvailabilityPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, token, loading: authLoading } = useAuth();
  const [availabilities, setAvailabilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copySource, setCopySource] = useState(null);
  const [expandedMonths, setExpandedMonths] = useState({});
  const [deleting, setDeleting] = useState({});

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    
    if (user && !ADMIN_EMAILS.includes(user.email)) {
      toast.error('Admin access required');
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user && ADMIN_EMAILS.includes(user.email)) {
      fetchAllAvailabilities();
    }
  }, [user]);

  const fetchAllAvailabilities = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/availability/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailabilities(response.data);
      
      // Auto-expand current and next month
      const now = new Date();
      const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      const nextMonthKey = `${nextMonth.getFullYear()}-${String(nextMonth.getMonth() + 1).padStart(2, '0')}`;
      
      setExpandedMonths({ [currentMonthKey]: true, [nextMonthKey]: true });
    } catch (error) {
      console.error('Error fetching availabilities:', error);
      toast.error(t('Could not delete availability'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAvailability = async (instructorId, date) => {
    if (!window.confirm(`${t('Are you sure you want to delete availability for')} ${date}?`)) {
      return;
    }

    const key = `${instructorId}-${date}`;
    setDeleting(prev => ({ ...prev, [key]: true }));

    try {
      await axios.delete(`${API}/admin/availability/${instructorId}/${date}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('Availability deleted'));
      fetchAllAvailabilities();
    } catch (error) {
      console.error('Error deleting availability:', error);
      toast.error(error.response?.data?.detail || t('Could not delete availability'));
    } finally {
      setDeleting(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleEditAvailability = (instructorId, date) => {
    navigate(`/admin/availability/edit/${instructorId}/${date}`);
  };

  const handleCopyAvailability = (instructorId, date) => {
    setCopySource({ instructorId, date });
    setShowCopyModal(true);
  };

  const handleCloseCopyModal = () => {
    setShowCopyModal(false);
    setCopySource(null);
  };

  const handleConfirmCopy = async (numberOfWeeks) => {
    if (!copySource) return;

    try {
      const response = await axios.post(
        `${API}/admin/availability/copy`,
        {
          instructor_id: copySource.instructorId,
          source_date: copySource.date,
          number_of_weeks: numberOfWeeks
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { created, skipped, total_created, total_skipped } = response.data;
      
      if (total_created > 0) {
        toast.success(`${total_created} ${t('date(s) copied!')}`);
      }
      
      if (total_skipped > 0) {
        toast.info(`${total_skipped} ${t('date(s) skipped (already exists)')}`);
      }

      handleCloseCopyModal();
      fetchAllAvailabilities();
    } catch (error) {
      console.error('Error copying availability:', error);
      toast.error(error.response?.data?.detail || t('Could not copy availability'));
    }
  };

  const toggleMonth = (monthKey) => {
    setExpandedMonths(prev => ({
      ...prev,
      [monthKey]: !prev[monthKey]
    }));
  };

  // Group availabilities by month
  const groupByMonth = (availabilities) => {
    const grouped = {};
    
    availabilities.forEach(avail => {
      const date = new Date(avail.date + 'T00:00:00');
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!grouped[monthKey]) {
        grouped[monthKey] = {
          label: date.toLocaleDateString('da-DK', { year: 'numeric', month: 'long' }),
          items: []
        };
      }
      grouped[monthKey].items.push(avail);
    });
    
    // Sort months chronologically
    const sortedKeys = Object.keys(grouped).sort();
    const sorted = {};
    sortedKeys.forEach(key => {
      sorted[key] = grouped[key];
    });
    
    return sorted;
  };

  const getInstructorName = (instructorId) => {
    switch(instructorId) {
      case 'julie-christiansen': return 'Julie Christiansen';
      case 'oscar': return 'Óskar Erik';
      default: return instructorId;
    }
  };

  if (authLoading || !user) {
    return (
      <div>
        <Navbar />
        <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
          <LoadingSkeleton type="card" count={3} />
        </div>
      </div>
    );
  }

  if (!ADMIN_EMAILS.includes(user.email)) {
    return null;
  }

  const groupedAvailabilities = groupByMonth(availabilities);

  return (
    <div>
      <Navbar />
      <div className="booking-page" style={{ paddingTop: '100px', minHeight: '100vh', background: '#fafafa' }}>
        <div className="admin-container" style={{ maxWidth: '1000px', margin: '0 auto', padding: '2rem' }}>
          {/* Header with back button */}
          <div style={{ 
            background: 'white', 
            padding: '2rem', 
            borderRadius: '12px', 
            marginBottom: '2rem',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
          }}>
            <button
              onClick={() => navigate('/admin')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'none',
                border: 'none',
                color: '#7c6f5e',
                cursor: 'pointer',
                fontSize: '0.95rem',
                marginBottom: '1rem',
                padding: '0.5rem',
                borderRadius: '5px',
                transition: 'background 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.background = '#f5f5f5'}
              onMouseOut={(e) => e.currentTarget.style.background = 'none'}
            >
              <ArrowLeft size={20} />
              {t('Back to Admin Dashboard')}
            </button>
            <h1 className="booking-title" style={{ marginBottom: '0.5rem', fontSize: '2rem' }}>
              {t('Manage Availability')}
            </h1>
            <p style={{ color: '#666', marginBottom: '1rem', fontSize: '1rem' }}>
              {t('Create and manage instructor availability')}
            </p>
            <button
              onClick={() => navigate('/admin/availability/create')}
              className="cta-button"
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <Plus size={20} />
              {t('Add Availability')}
            </button>
          </div>

          {/* Availability Content - Grouped by Month */}
          {loading ? (
            <LoadingSkeleton type="card" count={3} />
          ) : availabilities.length === 0 ? (
            <div style={{
              background: 'white',
              padding: '3rem',
              borderRadius: '12px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
              textAlign: 'center'
            }}>
              <Calendar size={48} color="#ccc" style={{ marginBottom: '1rem' }} />
              <p style={{ color: '#666', marginBottom: '1rem' }}>
                {t('No availability slots created yet.')}
              </p>
              <p style={{ color: '#888', fontSize: '0.9rem' }}>
                {t('Click Add Availability to create new time slots for bookings.')}
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {Object.entries(groupedAvailabilities).map(([monthKey, monthData]) => (
                <div 
                  key={monthKey}
                  style={{
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                    overflow: 'hidden'
                  }}
                >
                  {/* Month Header - Clickable */}
                  <button
                    onClick={() => toggleMonth(monthKey)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '1.25rem 1.5rem',
                      background: expandedMonths[monthKey] ? '#7c6f5e' : '#f8f8f8',
                      color: expandedMonths[monthKey] ? 'white' : '#333',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      {expandedMonths[monthKey] ? (
                        <ChevronDown size={20} />
                      ) : (
                        <ChevronRight size={20} />
                      )}
                      <span style={{ 
                        fontSize: '1.1rem', 
                        fontWeight: 600,
                        textTransform: 'capitalize'
                      }}>
                        {monthData.label}
                      </span>
                    </div>
                    <span style={{ 
                      background: expandedMonths[monthKey] ? 'rgba(255,255,255,0.2)' : '#e0e0e0',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '20px',
                      fontSize: '0.85rem'
                    }}>
                      {monthData.items.length} {monthData.items.length !== 1 ? t('dates') : t('date')}
                    </span>
                  </button>

                  {/* Month Content - Expandable */}
                  {expandedMonths[monthKey] && (
                    <div style={{ padding: '1rem' }}>
                      {monthData.items.map((avail) => {
                        const dateObj = new Date(avail.date + 'T00:00:00');
                        const key = `${avail.instructor_id}-${avail.date}`;
                        const isDeleting = deleting[key];
                        
                        return (
                          <div
                            key={key}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '1rem',
                              background: '#fafafa',
                              borderRadius: '8px',
                              marginBottom: '0.5rem',
                              borderLeft: '4px solid #7c6f5e'
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, color: '#333', marginBottom: '0.25rem' }}>
                                {dateObj.toLocaleDateString('da-DK', {
                                  weekday: 'long',
                                  day: 'numeric',
                                  month: 'short'
                                })}
                              </div>
                              <div style={{ fontSize: '0.85rem', color: '#666' }}>
                                {getInstructorName(avail.instructor_id)} • {avail.time_slots?.length || 0} {t('slots')}
                                {avail.time_slots && avail.time_slots.length > 0 && (
                                  <span style={{ marginLeft: '0.5rem' }}>
                                    ({avail.time_slots[0].start_time} - {avail.time_slots[avail.time_slots.length - 1].end_time})
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button
                                onClick={() => handleCopyAvailability(avail.instructor_id, avail.date)}
                                title={t('Copy to next weeks')}
                                style={{
                                  padding: '0.5rem',
                                  background: '#e3f2fd',
                                  color: '#1976d2',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <Copy size={16} />
                              </button>
                              <button
                                onClick={() => handleEditAvailability(avail.instructor_id, avail.date)}
                                title={t('Edit')}
                                style={{
                                  padding: '0.5rem',
                                  background: '#f5f0eb',
                                  color: '#7c6f5e',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <Edit size={16} />
                              </button>
                              <button
                                onClick={() => handleDeleteAvailability(avail.instructor_id, avail.date)}
                                disabled={isDeleting}
                                title={t('Delete')}
                                style={{
                                  padding: '0.5rem',
                                  background: isDeleting ? '#ffcdd2' : '#ffebee',
                                  color: '#c62828',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: isDeleting ? 'wait' : 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  opacity: isDeleting ? 0.6 : 1
                                }}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Copy Availability Modal */}
      {showCopyModal && copySource && (
        <CopyAvailabilityModal
          sourceDate={copySource.date}
          instructorId={copySource.instructorId}
          onClose={handleCloseCopyModal}
          onConfirm={handleConfirmCopy}
        />
      )}
    </div>
  );
};

export default AdminAvailabilityPage;
